import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { UserPlus, Play, Check, X, RefreshCw, AlertTriangle, MapPin, Mail, Phone, Eye } from 'lucide-react';
import { vendorDiscoveryAPI, geoAnalyticsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const RED = '#dc2626';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

const INPUT = { padding: '8px 11px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13.5, fontFamily: 'var(--font-sans)', boxSizing: 'border-box' };
const fmtDt = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export default function VendorDiscovery() {
  useAuth();
  const [taxo, setTaxo] = useState(null);                 // categories/subCategories from geo filters
  const [scope, setScope] = useState({ categoryId: '', subCategoryId: '' });
  const [plan, setPlan] = useState(null);
  const [showPlan, setShowPlan] = useState(false);
  const [data, setData] = useState(null);                 // { prospects, runs, status }
  const [tab, setTab] = useState('pending');
  const [flt, setFlt] = useState({ state: '', city: '', hasEmail: false });
  const [sel, setSel] = useState({});
  const [edits, setEdits] = useState({});                 // id -> { email, phone }
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    geoAnalyticsAPI.filters().then(r => setTaxo(r.data.data || r.data)).catch(() => setErr('Failed to load categories'));
  }, []);

  const load = useCallback(() => {
    const p = { status: tab };
    if (scope.categoryId) p.categoryId = scope.categoryId;
    if (scope.subCategoryId) p.subCategoryId = scope.subCategoryId;
    return vendorDiscoveryAPI.list(p)
      .then(r => setData(r.data.data || r.data))
      .catch(e => setErr(e.response?.data?.message || 'Failed to load vendor discovery'));
  }, [tab, scope]);
  useEffect(() => { load(); setSel({}); }, [load]);

  // poll while a run is in progress
  useEffect(() => {
    if (data?.status?.running && !pollRef.current) pollRef.current = setInterval(() => load(), 6000);
    else if (!data?.status?.running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [data?.status?.running, load]);

  const subOptions = useMemo(() => {
    if (!taxo) return [];
    if (!scope.categoryId) return [];
    return taxo.subCategories.filter(s => (s.categories || []).map(String).includes(String(scope.categoryId)));
  }, [taxo, scope.categoryId]);

  const st = data?.status || {};
  const runs = data?.runs || [];
  const lastRun = runs[0];

  // client-side state/city/email filters (options derived from loaded prospects)
  const allProspects = data?.prospects || [];
  const stateOptions = useMemo(() => [...new Set(allProspects.map(p => p.state).filter(Boolean))].sort(), [allProspects]);
  const cityOptions = useMemo(() => [...new Set(allProspects.filter(p => !flt.state || p.state === flt.state).map(p => p.city).filter(Boolean))].sort(), [allProspects, flt.state]);
  const prospects = useMemo(() => allProspects.filter(p =>
    (!flt.state || p.state === flt.state) &&
    (!flt.city || p.city === flt.city) &&
    (!flt.hasEmail || !!p.email)
  ), [allProspects, flt]);

  const selIds = Object.keys(sel).filter(id => sel[id]);
  const selectableIds = prospects.filter(p => p.status === 'pending').map(p => p._id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => sel[id]);

  const previewPlan = () => {
    if (!scope.categoryId) { setErr('Pick a category first.'); return; }
    setBusy('plan'); setErr('');
    vendorDiscoveryAPI.plan(scope)
      .then(r => { setPlan(r.data.data || r.data); setShowPlan(true); })
      .catch(e => setErr(e.response?.data?.message || 'Could not build the city plan'))
      .finally(() => setBusy(''));
  };
  const runNow = () => {
    if (!scope.categoryId) { setErr('Pick a category first.'); return; }
    setBusy('run'); setMsg(''); setErr('');
    vendorDiscoveryAPI.run(scope)
      .then(r => { setMsg(r.data.message || 'Run started.'); setShowPlan(false); setTimeout(() => load(), 2500); })
      .catch(e => setErr(e.response?.data?.message || 'Could not start run'))
      .finally(() => setBusy(''));
  };
  const saveEdit = (p) => {
    const e = edits[p._id];
    if (!e) return;
    vendorDiscoveryAPI.update(p._id, e)
      .then(() => load())
      .catch(x => setErr(x.response?.data?.message || 'Could not save contact details'));
  };
  const importIds = (ids) => {
    if (!ids.length) return;
    setBusy('import'); setMsg(''); setErr('');
    vendorDiscoveryAPI.importSel(ids)
      .then(r => {
        const d = r.data.data || {};
        setMsg(`Import done — ${d.imported || 0} vendor account(s) created & invited${d.existing ? `, ${d.existing} already had accounts` : ''}${d.noEmail ? `, ${d.noEmail} skipped (no email)` : ''}${d.emailFailed ? `, ${d.emailFailed} email(s) failed` : ''}.`);
        setSel({}); load();
      })
      .catch(e => setErr(e.response?.data?.message || 'Import failed'))
      .finally(() => setBusy(''));
  };
  const reject = (p) => {
    setBusy(p._id);
    vendorDiscoveryAPI.reject(p._id, '')
      .then(() => { setMsg(`Rejected "${p.firmName}" — it won't be suggested again.`); load(); })
      .catch(e => setErr(e.response?.data?.message || 'Reject failed'))
      .finally(() => setBusy(''));
  };
  const setEdit = (id, patch) => setEdits(e => ({ ...e, [id]: { ...(e[id] || {}), ...patch } }));

  if (!taxo) return <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center', color: MUTED }}>Loading vendor discovery…</div>;

  return (
    <div style={{ padding: '32px 40px', background: CREAM, minHeight: '100vh' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 800, color: CHAR, margin: 0, letterSpacing: '-0.02em' }}>
          <UserPlus size={26} style={{ verticalAlign: -4, marginRight: 8, color: GREEN }} />Vendor Discovery
        </h1>
        <p style={{ color: MUTED, fontSize: 15, marginTop: 6, fontWeight: 500, maxWidth: 900, lineHeight: 1.55 }}>
          Pick a category and the AI scans the <b style={{ color: CHAR }}>geo-analysis top cities</b> (top 5 cities × top 10 states for that category's market)
          for real vendor businesses to approach. Review, fix contact details, then <b style={{ color: CHAR }}>Import</b> — accounts are created and the onboarding
          invitation email goes out automatically.
        </p>
      </div>

      {/* scope + run */}
      <div className="card" style={{ padding: 20, marginBottom: 16, display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>Category *</label><br />
          <select style={{ ...INPUT, minWidth: 200 }} value={scope.categoryId} onChange={e => { setScope({ categoryId: e.target.value, subCategoryId: '' }); setShowPlan(false); }}>
            <option value="">— pick a category —</option>
            {taxo.categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>Sub-category (optional)</label><br />
          <select style={{ ...INPUT, minWidth: 200 }} value={scope.subCategoryId} onChange={e => { setScope(s => ({ ...s, subCategoryId: e.target.value })); setShowPlan(false); }} disabled={!scope.categoryId}>
            <option value="">All sub-categories</option>
            {subOptions.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
        </div>
        <button onClick={previewPlan} disabled={!scope.categoryId || busy === 'plan'} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, border: `1.5px solid ${PURPLE}`, cursor: 'pointer', background: '#fff', color: PURPLE, fontSize: 13, fontWeight: 700, opacity: !scope.categoryId ? 0.5 : 1 }}>
          <Eye size={15} />{busy === 'plan' ? 'Building…' : 'Preview cities'}
        </button>
        <button onClick={runNow} disabled={!scope.categoryId || st.running || busy === 'run' || !st.keyConfigured} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', background: st.running ? GOLD : GREEN, color: '#fff', fontSize: 13.5, fontWeight: 700, opacity: (!scope.categoryId || !st.keyConfigured) ? 0.5 : 1 }}>
          {st.running ? <RefreshCw size={15} className="spinv" /> : <Play size={15} />}
          {st.running ? 'Scanning cities…' : 'Discover vendors'}
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 12.5, color: st.keyConfigured ? MUTED : RED, fontWeight: 600 }}>
          {st.keyConfigured ? `AI: ${st.provider} · ${st.model}` : '⚠ AI key not configured (backend .env)'}
          {lastRun && <span style={{ color: MUTED }}> · last run {fmtDt(lastRun.startedAt)}: {lastRun.status}, {lastRun.prospectsFound} found / {lastRun.citiesScanned} cities</span>}
        </div>
      </div>
      <style>{`.spinv { animation: spinv 1.2s linear infinite; } @keyframes spinv { to { transform: rotate(360deg); } }`}</style>

      {/* city plan preview */}
      {showPlan && plan && (
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: CHAR, marginBottom: 10 }}>
            <MapPin size={15} style={{ verticalAlign: -2, color: GREEN }} /> Scan plan for <span style={{ color: PURPLE }}>{plan.subCategory ? `${plan.subCategory} · ` : ''}{plan.category}</span>
            <span style={{ color: MUTED, fontWeight: 500 }}> — market profile: {plan.profileLabel} · ≈{plan.estimatedCalls} city searches</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {plan.plan.map(p => (
              <div key={p.state} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
                <span style={{ minWidth: 150, fontWeight: 700, color: CHAR }}>{p.state} <span style={{ color: MUTED, fontWeight: 500 }}>({p.marketIndex})</span></span>
                {p.cities.map((c, i) => <span key={i} style={{ padding: '3px 10px', background: 'rgba(27,92,58,0.08)', color: GREEN, borderRadius: 10, fontWeight: 600 }}>{c || 'major cities'}</span>)}
              </div>
            ))}
          </div>
        </div>
      )}

      {msg && <div style={{ padding: '10px 16px', marginBottom: 14, borderRadius: 10, background: 'rgba(27,92,58,0.08)', color: GREEN, fontSize: 13.5, fontWeight: 600 }}>{msg}</div>}
      {err && <div style={{ padding: '10px 16px', marginBottom: 14, borderRadius: 10, background: 'rgba(220,38,38,0.07)', color: RED, fontSize: 13.5, fontWeight: 600 }}>{err}</div>}

      {/* tabs + filters + bulk */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        {[['pending', `Pending (${st.counts?.pending ?? 0})`], ['imported', `Imported (${st.counts?.imported ?? 0})`], ['rejected', `Rejected (${st.counts?.rejected ?? 0})`]].map(([k, t]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 16px', borderRadius: 18, cursor: 'pointer', fontSize: 13, fontWeight: 700, border: `1.5px solid ${tab === k ? GREEN : BORDER}`, background: tab === k ? GREEN : '#fff', color: tab === k ? '#fff' : MUTED }}>{t}</button>
        ))}
        <select style={{ ...INPUT, minWidth: 140 }} value={flt.state} onChange={e => setFlt(f => ({ ...f, state: e.target.value, city: '' }))}>
          <option value="">All states</option>
          {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select style={{ ...INPUT, minWidth: 140 }} value={flt.city} onChange={e => setFlt(f => ({ ...f, city: e.target.value }))}>
          <option value="">All cities</option>
          {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
          <input type="checkbox" checked={flt.hasEmail} onChange={e => setFlt(f => ({ ...f, hasEmail: e.target.checked }))} /> has email
        </label>
        <div style={{ flex: 1 }} />
        {tab === 'pending' && prospects.length > 0 && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
              <input type="checkbox" checked={allSelected} onChange={e => { const v = e.target.checked; setSel(Object.fromEntries(selectableIds.map(id => [id, v]))); }} /> select all
            </label>
            {selIds.length > 0 && (
              <>
                <button onClick={() => importIds(selIds)} disabled={busy === 'import'} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: GREEN, color: '#fff', fontSize: 12.5, fontWeight: 700 }}>
                  {busy === 'import' ? 'Importing…' : `Import ${selIds.length} selected`}
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* prospects */}
      {prospects.length === 0 ? (
        <div className="card" style={{ padding: '48px 24px', textAlign: 'center', color: MUTED }}>
          <UserPlus size={36} style={{ opacity: 0.3 }} />
          <p style={{ fontSize: 15, fontWeight: 600, margin: '12px 0 4px', color: CHAR }}>No {tab} prospects{scope.categoryId ? ' for this selection' : ''}</p>
          <p style={{ fontSize: 13.5, fontWeight: 500, margin: 0 }}>Pick a category and hit "Discover vendors" to scan the top cities.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {prospects.map(p => {
            const e = edits[p._id] || {};
            const pending = p.status === 'pending';
            const email = e.email ?? p.email;
            return (
              <div key={p._id} className="card" style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start', borderLeft: `4px solid ${p.status === 'imported' ? GREEN : p.status === 'rejected' ? BORDER : GOLD}` }}>
                {pending && <input type="checkbox" checked={!!sel[p._id]} onChange={ev => setSel(x => ({ ...x, [p._id]: ev.target.checked }))} style={{ marginTop: 5, width: 16, height: 16, cursor: 'pointer' }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, fontSize: 15, color: CHAR }}>{p.firmName}</span>
                    {p.contactName && <span style={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>{p.contactName}</span>}
                    <span style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 9px', background: 'rgba(76,29,149,0.08)', color: PURPLE, borderRadius: 10 }}>{p.categoryId?.name}{p.subCategoryId?.name ? ` › ${p.subCategoryId.name}` : ''}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 9px', background: '#f3f4f6', color: MUTED, borderRadius: 10 }}><MapPin size={11} style={{ verticalAlign: -1 }} /> {p.city}{p.state ? `, ${p.state}` : ''}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 9px', background: '#f3f4f6', color: MUTED, borderRadius: 10 }}>{(p.confidence * 100).toFixed(0)}%</span>
                  </div>
                  {p.address && <div style={{ fontSize: 12.5, color: MUTED, marginTop: 4 }}>{p.address}</div>}
                  <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {pending ? (
                      <>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Mail size={13} style={{ color: email ? GREEN : MUTED }} />
                          <input style={{ ...INPUT, width: 230, padding: '5px 9px', fontSize: 12.5 }} placeholder="no email — add to enable import" value={email} onChange={ev => setEdit(p._id, { email: ev.target.value })} onBlur={() => e.email !== undefined && saveEdit(p)} />
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Phone size={13} style={{ color: (e.phone ?? p.phone) ? GREEN : MUTED }} />
                          <input style={{ ...INPUT, width: 150, padding: '5px 9px', fontSize: 12.5 }} placeholder="phone" value={e.phone ?? p.phone} onChange={ev => setEdit(p._id, { phone: ev.target.value })} onBlur={() => e.phone !== undefined && saveEdit(p)} />
                        </span>
                      </>
                    ) : (
                      <span style={{ fontSize: 12.5, color: MUTED, fontWeight: 600 }}>
                        {p.email ? <><Mail size={12} style={{ verticalAlign: -1 }} /> {p.email}</> : 'no email'}{p.phone ? <> · <Phone size={12} style={{ verticalAlign: -1 }} /> {p.phone}</> : ''}
                      </span>
                    )}
                  </div>
                  {p.reason && <div style={{ fontSize: 12, color: MUTED, fontStyle: 'italic', marginTop: 6 }}>AI: {p.reason}</div>}
                  {p.status === 'imported' && (
                    <div style={{ fontSize: 12, marginTop: 6, fontWeight: 600, color: p.emailStatus === 'sent' ? GREEN : p.emailStatus === 'failed' ? RED : MUTED }}>
                      {p.note || (p.emailStatus === 'sent' ? `✓ account created · invite emailed ${fmtDt(p.invitedAt)}` : p.emailStatus === 'failed' ? 'account created but invite email FAILED' : 'imported')}
                    </div>
                  )}
                </div>
                {pending && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => importIds([p._id])} disabled={!email || busy === 'import'} title={email ? 'Create vendor account + send invite' : 'Add an email to enable import'} style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', padding: '8px 14px', borderRadius: 8, border: 'none', cursor: email ? 'pointer' : 'not-allowed', background: GREEN, color: '#fff', fontSize: 12.5, fontWeight: 700, opacity: email ? 1 : 0.45 }}>
                      <Check size={14} />Import + Invite
                    </button>
                    <button onClick={() => reject(p)} disabled={busy === p._id} style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${RED}`, cursor: 'pointer', background: '#fff', color: RED, fontSize: 12.5, fontWeight: 700 }}>
                      <X size={14} />Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ fontSize: 11.5, color: MUTED, marginTop: 20, textAlign: 'center', maxWidth: 900, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
        <AlertTriangle size={12} style={{ verticalAlign: -1 }} /> Contact details are AI-suggested leads, not verified data — the AI is told to leave unknown phones/emails empty rather than guess,
        but always sanity-check before serious outreach. Import creates a real vendor account and emails a one-time invitation; recipients who ignore it are never emailed again.
      </p>
    </div>
  );
}
